type RedirectUriDiagnostics = {
  configuredValue: string | undefined
  resolvedRedirectUri: string
  isUsingFallback: boolean
  warningMessage?: string
}

function isSuspiciousRedirectUri(value: string): boolean {
  const v = value.trim()
  if (!v) return true

  // Common misconfiguration: Azure DevOps / Terraform placeholders accidentally committed as literals.
  if (v.includes('${') || v.includes('}')) return true
  if (/\$\(.*\)/.test(v)) return true

  // Must be an absolute URL with http(s) scheme.
  if (!/^https?:\/\//i.test(v)) return true

  try {
    const url = new URL(v)
    if (!url.hostname || url.hostname.includes('$')) return true
    return false
  } catch {
    return true
  }
}

export function getRedirectUriDiagnostics(): RedirectUriDiagnostics {
  const configuredValue = import.meta.env.VITE_REDIRECT_URI?.trim()
  const fallback = `${window.location.origin}/login`

  if (!configuredValue) {
    return {
      configuredValue: undefined,
      resolvedRedirectUri: fallback,
      isUsingFallback: false,
    }
  }

  if (isSuspiciousRedirectUri(configuredValue)) {
    return {
      configuredValue,
      resolvedRedirectUri: fallback,
      isUsingFallback: true,
      warningMessage: `VITE_REDIRECT_URI looks misconfigured ("${configuredValue}"). Using ${fallback} instead. Fix the Azure DevOps pipeline variable VITE_REDIRECT_URI to a real absolute URL (for example https://mim.kingspan.app/login).`,
    }
  }

  const withScheme = /^https?:\/\//i.test(configuredValue)
    ? configuredValue
    : `https://${configuredValue.replace(/^\/+/, '')}`

  try {
    const resolvedRedirectUri = new URL(withScheme).toString()
    if (isSuspiciousRedirectUri(resolvedRedirectUri)) {
      return {
        configuredValue,
        resolvedRedirectUri: fallback,
        isUsingFallback: true,
        warningMessage: `Resolved VITE_REDIRECT_URI looks invalid ("${resolvedRedirectUri}"). Using ${fallback} instead.`,
      }
    }
    return {
      configuredValue,
      resolvedRedirectUri,
      isUsingFallback: false,
    }
  } catch {
    return {
      configuredValue,
      resolvedRedirectUri: fallback,
      isUsingFallback: true,
      warningMessage: `Invalid VITE_REDIRECT_URI "${configuredValue}". Falling back to ${fallback}.`,
    }
  }
}

export function resolveRedirectUri(): string {
  return getRedirectUriDiagnostics().resolvedRedirectUri
}
